import classNames from 'classnames'
import {
  differenceInCalendarMonths,
  endOfMonth,
  endOfYear,
  startOfMonth,
  subMonths,
} from 'date-fns'
import { FC, useEffect, useMemo, useState } from 'react'
import ReactDatePicker, { CalendarContainer, DatePickerProps } from 'react-datepicker'
import { Trans, useTranslation } from 'react-i18next'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Body1, Body2, Caption1 } from '@ui/Typography'
import { Tooltip } from '@ui/components/Tooltip'

import { getTwoYearsAgo } from '@utils/date.utils'
import { useWindowResize } from '@utils/domUtils'

import { dateFnsLocales } from '@global/locale/Locale'

import { CalendarHeader } from './CalendarHeader'
import { getAllContainerBounds, getDayRangeFlags } from './dayRangeClassName.utils'
import { useYearMonthNavigation } from './useYearMonthNavigation'

import styles from './Calendar.module.scss'

export const YEAR_ITEM_NUMBER = 12

type CalendarProps = DatePickerProps & {
  disableYearMonthMode?: boolean
  /**
   * Show extra information on the day box hover why a particular date selection isn't available if it's earlier than minDate
   */
  showEarlyDateLimitReason?: boolean
  openExportModal?: () => void
  closeCalendar?: () => void
}

export const Calendar: FC<CalendarProps> = (props) => {
  const {
    showMonthYearPicker,
    showYearPicker,
    disableYearMonthMode,
    onChange,
    onDayMouseEnter,
    showEarlyDateLimitReason,
    openExportModal,
    closeCalendar,
    ...restProps
  } = props
  const isMobile = useWindowResize()
  const locale = useSessionLanguage()
  const minDateThreshold = restProps.minDate ?? getTwoYearsAgo()
  const yearsCount = new Date().getFullYear() - minDateThreshold.getFullYear()
  const isDayTooOld = (date: Date) => date < minDateThreshold

  const isMonthTooOld = (date: Date) => endOfMonth(date) < minDateThreshold

  const isYearTooOld = (date: Date) => endOfYear(date) < minDateThreshold
  /**
   * Tracks the day the user is hovering over during range selection.
   * Used for dayHasAbove / dayHasBelow while endDate is still null.
   */
  const [hoverDate, setHoverDate] = useState<Date | null>(null)

  const enableYearMonthFlow = !disableYearMonthMode

  const { mode, setMode, navDate, handleChange, handleSelect } = useYearMonthNavigation({
    enabled: enableYearMonthFlow,
    selectsRange: props.selectsRange,
    minDate: restProps.minDate ?? undefined,
    maxDate: restProps.maxDate ?? undefined,
    onChange,
    onSelect: props.onSelect,
  })

  const effectiveShowYearPicker = enableYearMonthFlow ? mode === 'year' : !!showYearPicker
  const effectiveShowMonthYearPicker = enableYearMonthFlow
    ? mode === 'month'
    : !!showMonthYearPicker

  const effectiveMonthsShown = mode === 'day' ? props.monthsShown : 1
  const effectiveSelectsRange = mode === 'day' && props.selectsRange ? true : undefined

  const monthsShownCount = typeof effectiveMonthsShown === 'number' ? effectiveMonthsShown : 1

  /**
   * Computes which date the calendar should open to.
   * Ensures both ends of a selected range are visible when monthsShown > 1.
   * navDate takes priority — it is the target of year → month navigation.
   */
  const effectiveOpenToDate = useMemo(() => {
    const { startDate, endDate, openToDate, minDate, maxDate } = props

    // Month picker opens to the selected year; day grid opens to the selected month
    if (mode !== 'day') return navDate ?? openToDate
    if (navDate) return startOfMonth(navDate)

    if (startDate && endDate) {
      const startM = startOfMonth(startDate)
      const endM = startOfMonth(endDate)
      const span = differenceInCalendarMonths(endM, startM)
      // If the range fits within the visible months, anchor to start; otherwise scroll to show end
      return span <= monthsShownCount - 1 ? startM : subMonths(endM, monthsShownCount - 1)
    }

    if (endDate && !startDate) return subMonths(startOfMonth(endDate), monthsShownCount - 1)
    if (startDate) return startOfMonth(startDate)
    if (maxDate && new Date() > maxDate) return startOfMonth(maxDate)
    if (minDate && new Date() < minDate) return startOfMonth(minDate)

    return openToDate
  }, [
    mode,
    navDate,
    monthsShownCount,
    props.startDate,
    props.endDate,
    props.openToDate,
    props.minDate,
    props.maxDate,
  ])

  /**
   * Tracks the month displayed in the left-most calendar container.
   * Used to determine per-container grid bounds in getDayClassName.
   */
  const [displayedStartDate, setDisplayedStartDate] = useState<Date>(() => {
    const base = effectiveOpenToDate ?? props.startDate ?? new Date()
    return new Date(base.getFullYear(), base.getMonth(), 1)
  })

  // Keep displayedStartDate in sync when the calendar navigates programmatically
  useEffect(() => {
    if (effectiveOpenToDate) {
      setDisplayedStartDate(
        new Date(effectiveOpenToDate.getFullYear(), effectiveOpenToDate.getMonth(), 1)
      )
    }
  }, [effectiveOpenToDate])

  // Compute grid bounds once per render rather than recalculating inside every cell
  const containerBounds = useMemo(
    () => getAllContainerBounds(displayedStartDate, monthsShownCount),
    [displayedStartDate, monthsShownCount]
  )

  const getDayClassName = (date: Date): string => {
    const { hasAbove, hasBelow } = getDayRangeFlags({
      date,
      startDate: restProps.startDate ?? undefined,
      endDate: restProps.endDate ?? undefined,
      hoverDate,
      displayedStartDate,
      monthsShownCount,
      bounds: containerBounds,
    })

    return classNames(styles.day, {
      [styles.dayRange]: !!effectiveSelectsRange,
      [styles.dayHasAbove]: hasAbove,
      [styles.dayHasBelow]: hasBelow,
      [styles.dayDisabledWithTooltip]: showEarlyDateLimitReason && isDayTooOld(date),
    })
  }
  // pickerKey forces a remount when dates or mode changes, keeping the calendar in sync
  const pickerKey =
    mode === 'day'
      ? `d:${props.startDate?.getTime() ?? 'x'}-${
          props.endDate?.getTime() ?? 'x'
        }:${monthsShownCount}`
      : `m:${mode}`

  const ExportLimitByYearsDayInfo = (day: number, date: Date) => {
    if (!isDayTooOld(date)) {
      return <Body2>{day}</Body2>
    }

    return (
      <ExportLimitTooltip
        isMobile={isMobile}
        yearsCount={yearsCount}
        closeCalendar={closeCalendar}
        openExportModal={openExportModal}
      >
        <Body2>{day}</Body2>
      </ExportLimitTooltip>
    )
  }
  const ExportLimitByYearsMonthInfo = (
    month: number,
    shortMonthText: string,
    _fullMonthText: string,
    date: Date
  ) => {
    const monthDate = new Date(date.getFullYear(), month, 1)
    const MonthTypography = isMobile ? Body1 : Body2

    if (!isMonthTooOld(monthDate)) {
      return <MonthTypography>{shortMonthText}</MonthTypography>
    }

    return (
      <ExportLimitTooltip
        isMobile={isMobile}
        yearsCount={yearsCount}
        closeCalendar={closeCalendar}
        openExportModal={openExportModal}
      >
        <MonthTypography>{shortMonthText}</MonthTypography>
      </ExportLimitTooltip>
    )
  }
  const ExportLimitByYearsYearInfo = (year: number) => {
    const yearDate = new Date(year, 0, 1)
    const YearTypography = isMobile ? Body1 : Body2

    if (!isYearTooOld(yearDate)) {
      return <YearTypography>{year}</YearTypography>
    }

    return (
      <ExportLimitTooltip
        isMobile={isMobile}
        yearsCount={yearsCount}
        closeCalendar={closeCalendar}
        openExportModal={openExportModal}
      >
        <YearTypography>{year}</YearTypography>
      </ExportLimitTooltip>
    )
  }
  const getMonthClassName = (date: Date) => {
    const monthDate = startOfMonth(date)
    const isFuture = restProps.maxDate ? monthDate > startOfMonth(restProps.maxDate) : false

    return classNames(styles.month, {
      [styles.monthDisabledWithTooltip]:
        showEarlyDateLimitReason && isMonthTooOld(monthDate) && !isFuture,
    })
  }

  const getYearClassName = (date: Date) => {
    const yearDate = new Date(date.getFullYear(), 0, 1)
    const isFuture = restProps.maxDate
      ? yearDate > new Date(restProps.maxDate.getFullYear(), 0, 1)
      : false

    return classNames(styles.year, {
      [styles.yearDisabledWithTooltip]:
        showEarlyDateLimitReason && isYearTooOld(yearDate) && !isFuture,
    })
  }
  return (
    <ReactDatePicker
      key={pickerKey}
      {...restProps}
      // as any because of weird react-datepicker types
      onChange={handleChange as any}
      selectsMultiple={undefined}
      selectsRange={effectiveSelectsRange}
      monthsShown={effectiveMonthsShown}
      openToDate={effectiveOpenToDate}
      showYearPicker={effectiveShowYearPicker}
      showMonthYearPicker={effectiveShowMonthYearPicker}
      onSelect={handleSelect}
      locale={dateFnsLocales[locale]}
      calendarContainer={({ children, ...rest }) => (
        <CalendarContainer
          {...rest}
          className={classNames(styles.calendarContainer, {
            [styles.isDayMonthMode]: mode === 'month' || mode === 'year',
          })}
        >
          {children}
        </CalendarContainer>
      )}
      {...(showEarlyDateLimitReason
        ? {
            renderDayContents: ExportLimitByYearsDayInfo,
            renderMonthContent: ExportLimitByYearsMonthInfo,
            renderYearContent: ExportLimitByYearsYearInfo,
          }
        : {})}
      inline
      onMonthChange={(date) =>
        setDisplayedStartDate(new Date(date.getFullYear(), date.getMonth(), 1))
      }
      onDayMouseEnter={(date) => {
        setHoverDate(date)
        onDayMouseEnter?.(date)
      }}
      weekDayClassName={() => styles.weekDay}
      monthClassName={getMonthClassName}
      yearClassName={getYearClassName}
      dayClassName={getDayClassName}
      yearItemNumber={YEAR_ITEM_NUMBER}
      calendarStartDay={1}
      renderCustomHeader={(headerProps) => (
        <CalendarHeader
          {...headerProps}
          mode={enableYearMonthFlow ? mode : 'day'}
          onModeChange={enableYearMonthFlow ? setMode : undefined}
          locale={dateFnsLocales[locale]}
        />
      )}
    />
  )
}
interface ExportLimitTooltipProps {
  children: React.ReactNode
  isMobile: boolean
  yearsCount: number
  closeCalendar?: () => void
  openExportModal?: () => void
}

const ExportLimitTooltip: FC<ExportLimitTooltipProps> = ({
  children,
  isMobile,
  yearsCount,
  closeCalendar,
  openExportModal,
}) => {
  const { t } = useTranslation()
  const DescriptionElem = isMobile ? Body1 : Caption1

  return (
    <Tooltip
      heading={isMobile ? t('IB.Export.exportData') : undefined}
      content={({ close }) => (
        <DescriptionElem>
          <Trans
            i18nKey={t(
              'IB.Export.In order to get a larger data set, please use Export Hub feature'
            )}
            values={{ yearsCount }}
            components={[
              <DescriptionElem
                className={styles.limitByYearsHighlightText}
                onClick={(event) => {
                  event.stopPropagation()
                  close()
                  closeCalendar?.()
                  openExportModal?.()
                }}
              />,
            ]}
          />
        </DescriptionElem>
      )}
      direction='bottom'
      wrapperClassName={styles.calendarCellTooltip}
      textAlignCenter
    >
      {children}
    </Tooltip>
  )
}
